/**
 * Hour formula helpers shared by the scorer, the planner's loader and the
 * editor: the default (one metric over one lookback), the range key a group
 * reads, group weights normalized to 100, a stable dedupe key, and the
 * plain-words description ("CR 60 · ROI 40 over last 14 days").
 * For Media Devoted | Created By Ciro Autiero
 */
import { FLOW_METRIC_LABELS } from "../formula/catalog";
import { expandSubrefsWithWeights } from "../formula/master-expression-display";
import { resolveEffectiveExpression } from "../formula/scoring/score-group";
import { serializeSliders } from "../formula/serialize-sliders";
import type { FormulaSlidersBody } from "../voluum-flow-optimizer.types";
import type {
  DropGenHourFormula,
  DropGenHourFormulaGroup,
} from "./hour-profile.types";

export const HOUR_METRIC_LABELS: Record<string, string> = FLOW_METRIC_LABELS;

export function defaultHourFormula(
  lookbackPreset: string,
  metric = "cr",
): DropGenHourFormula {
  const sliders: FormulaSlidersBody = {
    metrics: [{ id: "m1", metric, weight: 100 }],
  };
  return {
    groups: [
      {
        id: "hours-1",
        name: "Hours",
        dateRange: lookbackPreset,
        mode: "sliders",
        sliders,
        expression: serializeSliders(sliders),
      },
    ],
    groupWeights: { "hours-1": 100 },
  };
}

/** The report a group reads: its preset, or `custom:<from>:<to>`. */
export function hourRangeKey(
  g: Pick<
    DropGenHourFormulaGroup,
    "dateRange" | "dateRangeCustomFrom" | "dateRangeCustomTo"
  >,
): string {
  return g.dateRange === "custom"
    ? `custom:${g.dateRangeCustomFrom ?? ""}:${g.dateRangeCustomTo ?? ""}`
    : g.dateRange;
}

export function parseHourRangeKey(
  key: string,
): { preset: string } | { custom: { from: string; to: string } } {
  if (!key.startsWith("custom:")) return { preset: key };
  const [, from = "", to = ""] = key.split(":");
  return { custom: { from, to } };
}

/** Group weights as shares of 100; negatives clamp to 0, all-zero = equal. */
export function normalizedHourGroupWeights(
  formula: DropGenHourFormula,
): Record<string, number> {
  const ids = formula.groups.map((g) => g.id);
  const raw = ids.map((id) => Math.max(0, formula.groupWeights?.[id] ?? 0));
  const total = raw.reduce((s, w) => s + w, 0);
  return Object.fromEntries(
    ids.map((id, i) => [id, total > 0 ? (raw[i] / total) * 100 : 100 / ids.length]),
  );
}

/** Two formulas with the same key score identically — the loader's dedupe. */
export function hourFormulaKey(formula: DropGenHourFormula): string {
  const weights = normalizedHourGroupWeights(formula);
  return JSON.stringify(
    formula.groups.map((g) => [
      hourRangeKey(g),
      resolveEffectiveExpression(g),
      Math.round(weights[g.id] * 100) / 100,
    ]),
  );
}

export function hourRangeLabel(key: string): string {
  const parsed = parseHourRangeKey(key);
  if ("custom" in parsed) return `${parsed.custom.from} → ${parsed.custom.to}`;
  return parsed.preset.replace(/-/g, " ");
}

/** "CR 60 · lowest CPC 40 over last 14 days" (+ "×70%" per group when several). */
export function describeHourFormula(formula: DropGenHourFormula): string {
  const weights = normalizedHourGroupWeights(formula);
  return formula.groups
    .map((g) => {
      const body =
        g.mode === "sliders"
          ? (g.sliders?.metrics ?? [])
              .filter((m) => m.metric && m.weight > 0)
              .map((m) => `${m.lowest ? "lowest " : ""}${HOUR_METRIC_LABELS[m.metric] ?? m.metric} ${Math.round(m.weight)}`)
              .join(" · ") || "no metric"
          : expandSubrefsWithWeights(
              g.blocks?.masterExpression ?? g.expression ?? "",
              g.blocks?.subformulas ?? [],
            ) || "no formula yet";
      const share =
        formula.groups.length > 1 ? ` ×${Math.round(weights[g.id] ?? 0)}%` : "";
      return `${body} over ${hourRangeLabel(hourRangeKey(g))}${share}`;
    })
    .join(" + ");
}

/** The formula a config ranks on: the saved one, else CR 100 over its lookback. */
export function effectiveHourFormula(config: {
  lookbackPreset: string;
  formula?: DropGenHourFormula;
}): DropGenHourFormula {
  return config.formula ?? defaultHourFormula(config.lookbackPreset);
}

/** The formula as the API takes it: slider rows still waiting for a metric
 *  are dropped (the editor keeps them on screen until one is picked). */
export function cleanHourFormula(formula: DropGenHourFormula): DropGenHourFormula {
  return {
    groups: formula.groups.map((g) => {
      if (g.mode !== "sliders") return g;
      const sliders = {
        metrics: (g.sliders?.metrics ?? []).filter((m) => Boolean(m.metric)),
      };
      return { ...g, sliders, expression: serializeSliders(sliders) };
    }),
    groupWeights: formula.groupWeights ?? {},
  };
}
